import type {
  FobDetails,
  FobPropositionSelection,
  StartingPriceFOOSelection,
} from '@mobi/betslip/types'
import type { FOOBetDetails } from '@core/Areas/ShareBet/LoadBet/types'
import type { BuildBetSelectionOptions } from '@core/Areas/ShareBet/LoadBet/buildBetSelection/types'
import type { QuickbetSelection } from '@core/Areas/Quickbet/types'
import { getFixedOddsEventDetails } from '@core/Areas/RaceCardFixedOnly/Data/api'
import { RaceProduct } from '@core/Areas/RaceCardFixedOnly'
import { FixedOddsMarketDisplayNames } from '@core/Areas/RaceCard/constants'
import { FOB_TICKET_PLACED_BET_TYPE } from '@core/Areas/ShareBet/LoadBet/constants'

export const buildFixedOddsOnlyRacingBet = async (
  betDetails: FOOBetDetails,
  opts: BuildBetSelectionOptions = {}
) => {
  const { competitionSeq, propositionSeq, competitionName, sportName, betType } = betDetails

  const event = await getFixedOddsEventDetails(getFOOEventType(sportName), 'races', competitionSeq)

  const proposition = event.propositions.find(
    proposition => proposition.sequenceNumber === propositionSeq
  )
  if (!proposition) throw new Error('Failed to match provided proposition')
  if (opts.shouldRemoveScratched && proposition.scratchTime) throw new Error('Starter is scratched')

  const isStartingPrice = betType === FixedOddsMarketDisplayNames.SP
  const marketName = isStartingPrice ? 'Starting Price' : event.marketName
  const shouldAllowPlaceInvestment = !isStartingPrice && event.isPlaceAvailable

  const selection: StartingPriceFOOSelection | FobPropositionSelection = isStartingPrice
    ? {
        type: 'fob-proposition',
        propositionSeq: propositionSeq.toString(),
        winPrice: null,
        placePrice: null,
        marketCode: 'SP',
        marketName: 'StartingPrice',
      }
    : {
        type: 'fob-proposition',
        propositionSeq: propositionSeq.toString(),
        winPrice: proposition.winReturn,
        winPriceLastSeen: proposition.winReturn,
        placePrice: proposition.placeReturn,
        placePriceLastSeen: proposition.placeReturn,
        priceSource: 'selection',
        multiLegBetType: betDetails.betType === FOB_TICKET_PLACED_BET_TYPE ? 'P' : 'W',
      }

  const details: Partial<FobDetails> = {
    competitionName: competitionName,
    event: competitionSeq,
    marketName,
    eventName: event.eventName,
    eventStartTime: event.eventStartTime,
    propositionName: proposition.name,
    sportIconCode: 'tab',
    sportName: '',
    tournamentName: '',
    handicapName: null,
  }

  const isEachWayAvailable = Boolean(
    !event.isPlaceAvailable && selection.winPrice && selection.placePrice
  )
  const quickbetSelection: QuickbetSelection = {
    bettingType: 'fixed-odds-racing',
    isEachWayAvailable,
    shouldAllowPlaceInvestment,
    selection,
    selectionDetails: details as FobDetails,
  }

  return quickbetSelection
}

// =============
// Local Helpers
// =============

function getFOOEventType(sportName: string | null): RaceProduct {
  const product = (sportName || '').toLowerCase()
  if (product.includes('feature')) return RaceProduct.FeatureRace
  if (product.includes('challenge')) return RaceProduct.Challenge
  if (product.includes('future')) return RaceProduct.FutureFinalField

  return RaceProduct.FixedOddsOnly
}
